import { Map } from 'immutable';
import { companyUnreadCounterSelector } from 'domain/chat/facadeSelectors';

import { currentCompanyIdSelector, unreadRequestsSelector } from 'domain/documents/documentSelector';
import { selector } from 'lib/selectors';
import {
  isDokkaSupportSelector,
  userGUIDSelector,
  roleSelector,
  workSpaceTypeSelector,
  organizationIdSelector,
} from 'domain/env/envSelector';

import { companiesFilter, companiesSort, checkPermissions, CompanyFactory } from 'domain/companies/helpers';
import { allowedExportFormatsForGrid } from 'domain/documents/helpers';
import { textMap, pagesSelector } from 'domain/journal/selectors';
import { JE_FIELDS_TO_BE_HIGHLIGHTED } from 'domain/journal/helper';

import { AMOUNT_REPRESENTATION_VALUES } from 'lib/amountRepresentationValues';

const companies = (state) => state.companies;
const documents = (state) => state.documents;

export const currentOrganizationId = (state) => state.organization.currentOrganization;
export const searchTermSelector = selector(companies, (c) => c.filter);
export const searchTextSelector = selector(companies, (c) => c.searchText);
export const searchTypeSelector = selector(companies, (c) => c.searchType);
export const searchSortSelector = selector(companies, (c) => c.sort);

export const refreshCompanySelector = selector(companies, (c) => c.refreshCompany);
export const configurationCompanySelector = selector(companies, (c) => c.configurationCompany);
export const companyIdSelector = selector(configurationCompanySelector, (c) => c.id);
export const shouldHighlightAmountSelector = selector(
  configurationCompanySelector,
  (c) => c.amounts_representation === AMOUNT_REPRESENTATION_VALUES.HIGHLIGHTED,
);
export const companySigningsSelector = selector(configurationCompanySelector, (c) => c.signings);
export const companyApplicationsSelector = selector(configurationCompanySelector, (c) => c.applications);

export const companyFeaturesStateSelector = selector(companies, (c) => c.companyFeatures);
export const companyGeneralFeaturesSelector = selector(companyFeaturesStateSelector, (f) => f.get('generalFeatures'));
export const companyGeneralFeatureFlagsSelector = selector(companyGeneralFeaturesSelector, (f) =>
  f.map((item) => item.get('value')),
);
export const companyDynamicFeaturesSelector = selector(companyFeaturesStateSelector, (state) =>
  state.get('dynamicFeatures'),
);

// this selector belongs to journal domain however can not be located there
// because of circular dependency on companyGeneralFeatureFlagsSelector

// highlighting value controled by toggler on doc is only true if feature itself is enabled,
// othervise it should return false, even if toggler default value is set to true
export const highlightPolygonsSelector = selector(
  textMap,
  companyGeneralFeatureFlagsSelector,
  (t, flags) => flags.get('polygon_highlighting') && t.highlightPolygons,
);

// True when the current company has the experimental_brain feature enabled.
export const companyExperimentalBrainEnabledSelector = selector(companyDynamicFeaturesSelector, (f) =>
  Boolean(f.get('experimental_brain')),
);

// These two selectors belong to the journal domain conceptually, but must live here
// to avoid a circular dependency (companiesSelector → journal/selectors already exists).
//
// Polygon highlights are shown unfiltered only for companies with experimental_brain
// enabled. For all other companies the legacy whitelist (JE_FIELDS_TO_BE_HIGHLIGHTED)
// is applied so only the three core header fields are highlighted.
export const highlightedPolygonsFilteredSelector = (page) =>
  selector(pagesSelector, companyExperimentalBrainEnabledSelector, (pages, isExperimentalBrain) => {
    const polygons = pages.get(page)?.highlightedPolygons || new Map();
    return isExperimentalBrain
      ? polygons
      : polygons.filter((_, key) => JE_FIELDS_TO_BE_HIGHLIGHTED.includes(key));
  });

export const documentHasHighlightedPolygonsSelector = selector(
  pagesSelector,
  companyExperimentalBrainEnabledSelector,
  (pages, isExperimentalBrain) =>
    pages.some((page) => {
      const polygons = page.highlightedPolygons || new Map();
      return isExperimentalBrain
        ? polygons.size > 0
        : JE_FIELDS_TO_BE_HIGHLIGHTED.some((key) => polygons.has(key));
    }),
);

export const companyFeatureSetLoadedSelector = selector(companyFeaturesStateSelector, (state) => state.get('isLoaded'));

export const companyFeaturesCompanyIdSelector = selector(companyFeaturesStateSelector, (state) => state.companyID);

export const companySigningsCompanyIdSelector = selector(configurationCompanySelector, (c) => c.signingsCompanyId);

export const companyApprovalsFeatureEnabledSelector = selector(companyGeneralFeatureFlagsSelector, (f) =>
  f.get('approvals'),
);

export const companyApprovalsStampFeatureEnabledSelector = selector(companyGeneralFeatureFlagsSelector, (f) =>
  f.get('approval_stamp'),
);
export const companyPaidUnpaidFeatureEnabledSelector = selector(companyGeneralFeatureFlagsSelector, (f) =>
  f.get('paid_unpaid'),
);
export const companyEnableClientChatFeatureSelector = selector(companyGeneralFeatureFlagsSelector, (f) =>
  f.get('enable_client_chat'),
);

export const twoWayMatchingFeatureValueSelector = selector(companyGeneralFeatureFlagsSelector, (f) =>
  f.get('two_ways_matching'),
);

export const companiesByIdSelector = selector(companies, (c) => c.list);

export const isCompanyListLoadedSelector = selector(companies, (c) => c.isListLoaded);
export const isCompanyConfigurationLoadedSelector = selector(companies, (c) => c.isConfigurationLoaded);

export const getCurrentCompanyData = selector(
  companiesByIdSelector,
  currentCompanyIdSelector,
  (allCompanies, currentCompanyId) => allCompanies.get(currentCompanyId),
);

export const currentCompanyNameSelector = selector(companiesByIdSelector, currentCompanyIdSelector, (list, id) =>
  list.get(id, CompanyFactory()).get('cname'),
);

export const currentCompanyDateFormatSelector = selector(companiesByIdSelector, currentCompanyIdSelector, (list, id) =>
  list.get(id, CompanyFactory()).get('dateFormat'),
);

export const companyNotificationsSelector = companyUnreadCounterSelector;

export const organizationNotificationsSelector = selector(
  companyNotificationsSelector,
  companiesByIdSelector,
  (notifications, companyList) =>
    notifications.reduce((a, counter, companyId) => {
      const { organizationId } = companyList.get(companyId, {});
      if (typeof organizationId === 'undefined') {
        return a;
      }
      return a.update(organizationId, (u) => (u || 0) + counter);
    }, new Map()),
);

export const organizationNotificationsAndUnreadRequestsSelector = selector(
  organizationNotificationsSelector,
  unreadRequestsSelector,
  companiesByIdSelector,
  (notifications, requests, companyList) =>
    requests.reduce((a, request) => {
      const { organizationId } = companyList.get(request.companyId, {});
      if (typeof organizationId === 'undefined') {
        return a;
      }
      return a.update(organizationId, (u) => (u || 0) + 1);
    }, notifications),
);

export const companiesListSelector = selector(
  companiesByIdSelector,
  searchSortSelector,
  companyNotificationsSelector,
  companiesSort,
);

export const companiesSelector = selector(
  companiesListSelector,
  searchTermSelector,
  isDokkaSupportSelector,
  companiesFilter,
);

export const companiesByOrganizationSelector = selector(
  companiesSelector,
  currentOrganizationId,
  (companyList, currentOrganization) =>
    currentOrganization === null
      ? companyList
      : companyList.filter((company) => company.organizationId === currentOrganization),
);

export const allCompaniesByOrganizationSelector = selector(
  companiesListSelector,
  currentOrganizationId,
  (companyList, currentOrganization) =>
    currentOrganization === null
      ? companyList
      : companyList.filter((company) => company.organizationId === currentOrganization),
);

export const doesCurrentCompanySupportGeneralDocsOnlySelector = selector(
  documents,
  companiesByIdSelector,
  (d, c) => !c.getIn([d.companyId, 'generalParams', 'fin', 'value'], false),
);

export const isSignAvailableSelector = selector(
  userGUIDSelector,
  roleSelector,
  currentCompanyIdSelector,
  companiesByIdSelector,
  (id, role, companyId, list) => {
    const sp = list.getIn([companyId, 'signParams']);
    return Map({
      token1: checkPermissions(sp.getIn(['token1', 'permissions']), id, role),
      token2: checkPermissions(sp.getIn(['token2', 'permissions']), id, role),
    });
  },
);

export const availableErpsForCompanySelector = selector(companyFeaturesStateSelector, (state) => state.erps);

export const documentWorkSpaceTypeSelector = selector(
  workSpaceTypeSelector,
  currentCompanyIdSelector,
  (ws, companyId) => ws.get(companyId) || 'tile',
);

export const isGridWorkSpaceSelector = selector(documentWorkSpaceTypeSelector, (wsType) => wsType === 'grid');

export const exportFormatsSelector = selector(documents, isGridWorkSpaceSelector, (d, isGridView) => {
  const allFormats = d.exportFormats;
  return isGridView ? allFormats.filter((v, k) => allowedExportFormatsForGrid.includes(k)) : allFormats;
});

export const companiesWorkSpaceSelector = selector(companies, (c) => c.companiesWorkSpace);

export const companiesWorkSpaceTypeSelector = selector(
  companiesWorkSpaceSelector,
  organizationIdSelector,
  (ws, orgId) => ws.get(orgId) || 'tile',
);
